import { useReactiveVar } from '@apollo/client'
import { Flex } from '@radix-ui/themes'
import { privyEmbeddedAddressesVar } from 'features/auth-privy/store'
import { WalletAppList } from 'features/wallets/components/wallet-app-list'
import { useConnectLinkedWallet } from 'features/wallets/hooks/use-connect-linked-wallet'
import { DataTestIDs } from 'shared/constants'
import { XyroTokenMetallic } from 'shared/icons'
import { userVar } from 'shared/store/user'
import { RadixText, XyroLoading } from 'shared/ui'
import { useConnectWalletApp } from '../../hooks/use-connect-wallet-app'
import { isConnectWalletOpenVar } from '../../store/dialogs'
import { AuthButton } from '../auth-buttons'

/** The XYRO wallet Privy created for the user, when it is linked to the account. */
const useXyroWalletAddress = () => {
  const user = useReactiveVar(userVar)
  const embeddedAddresses = useReactiveVar(privyEmbeddedAddressesVar)

  return (
    user?.wallets?.find(wallet => embeddedAddresses.includes(wallet.address))
      ?.address ?? null
  )
}

/**
 * The wallets a signed-in user can connect: the XYRO wallet first, then the
 * wallet apps. The XYRO wallet has no app of its own, so without it here a
 * user whose chosen app stopped connecting could reach it only through Your
 * wallets. The dialog closes once a wallet is connected.
 */
export const WalletAppConnectors: React.FC = () => {
  const { connectApp, isConnecting } = useConnectWalletApp()
  const { connectWallet, pendingAddress } = useConnectLinkedWallet()
  const xyroWalletAddress = useXyroWalletAddress()
  const isBusy = isConnecting || pendingAddress !== null

  return (
    <Flex
      direction='column'
      justify='center'
      align='stretch'
      width='100%'
      gap='4'
    >
      {xyroWalletAddress ?
        <AuthButton
          disabled={isBusy}
          onClick={async () => {
            if (await connectWallet(xyroWalletAddress)) {
              isConnectWalletOpenVar(false)
            }
          }}
          dataTestID={DataTestIDs.buttonConnectXyroWallet}
        >
          <XyroLoading loading={pendingAddress === xyroWalletAddress}>
            <XyroTokenMetallic
              width='4.5rem'
              height='4.5rem'
            />
            <RadixText>XYRO wallet</RadixText>
          </XyroLoading>
        </AuthButton>
      : null}
      <WalletAppList
        isBusy={isBusy}
        buttonTestId={DataTestIDs.buttonConnectWalletApp}
        onPick={async provider => {
          if (await connectApp(provider)) isConnectWalletOpenVar(false)
        }}
      />
    </Flex>
  )
}
